import { useQuery } from '@tanstack/react-query'
import { Banknote, CalendarX, Info, Wallet } from 'lucide-react'

import { businessDayQueryOptions } from '@/api/reference-api'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { formatBusinessDate } from '@/lib/datetime'

import { SAVINGS_PERMISSIONS, useSavingsPermissions } from './permissions'

/**
 * The two things that refuse a cash posting after the customer has already handed over the notes.
 *
 * **The branch business day.** Postings land on the branch's open day, not on the browser's date.
 * A branch that has not opened today refuses everything with `ledger.business_day_closed`, and the
 * ledger is the authority on which day that is — this reads it rather than guessing.
 *
 * **The till.** Every cash-moving savings endpoint — deposit, withdrawal, security-deposit top-up,
 * and the refund a closure pays out — resolves the caller's own open counter session, or a field
 * till assigned to them for today, and otherwise fails `teller.no_open_till`. On a counter till it
 * additionally demands `teller.deposit.post` for cash in and `teller.withdrawal.post` for cash out,
 * and it can still refuse a posting that fits both with `teller.till_cash_limit_exceeded` or
 * `teller.till_insufficient_cash`. A transfer is the exception on this screen: it passes no till
 * type and touches no drawer, so none of this applies to it.
 *
 * What this panel does **not** do is claim to know whether a till is open. Nothing in the savings
 * contract answers that question, and the Tellering endpoints that could are another module's to
 * read. So it states the requirement, reports the half the browser genuinely knows — whether this
 * user's role holds the drawer's own permissions — and leaves the rest to be found at the counter
 * rather than asserted here. Guessing "your till is open" would be worse than saying nothing.
 */
export function CashPreconditions({
  branchId,
  /** A transfer needs the business day but no drawer, so the till half is dropped for it. */
  needsTill = true,
  /** Cash out has a different drawer permission from cash in. */
  direction = 'in',
}: {
  branchId: string | null
  needsTill?: boolean
  direction?: 'in' | 'out'
}) {
  const permissions = useSavingsPermissions()
  const businessDay = useQuery(businessDayQueryOptions(branchId))

  const tillPermission =
    direction === 'in' ? SAVINGS_PERMISSIONS.tellerDepositPost : SAVINGS_PERMISSIONS.tellerWithdrawalPost
  const holdsTillPermission = direction === 'in' ? permissions.canMoveCashIn : permissions.canMoveCashOut

  return (
    <div className="space-y-2">
      {branchId === null ? (
        /*
          The business-day query is disabled without a branch, and a disabled query stays `pending`
          for ever — so this cannot be left to the branch below it or the panel simply goes quiet
          about the one thing it exists to say. No branch selected means nothing has been read.
        */
        <Alert tone="warning">
          <CalendarX aria-hidden />
          <div className="space-y-1">
            <AlertTitle>No branch is selected</AlertTitle>
            <AlertDescription>
              A posting lands on a branch&apos;s open business day, and no branch has been chosen in
              the header — so this screen has read no business date at all and is not guessing at
              one. Choose the branch you are posting at first.
            </AlertDescription>
          </div>
        </Alert>
      ) : businessDay.isPending ? null : businessDay.isError || !businessDay.data ? (
        <Alert tone="warning">
          <CalendarX aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The branch business date could not be read</AlertTitle>
            <AlertDescription>
              The ledger did not answer, so this screen cannot say which day a posting would land on.
              It is not substituting today&apos;s date for it. Post only if you know the day is open.
            </AlertDescription>
          </div>
        </Alert>
      ) : !businessDay.data.acceptsPostings ? (
        <Alert tone="danger">
          <CalendarX aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The branch day is not accepting postings</AlertTitle>
            <AlertDescription>
              {businessDay.data.businessDate === null ? (
                <>
                  No business day is open at this branch
                  {businessDay.data.lastClosedBusinessDate ? (
                    <> — the last one was {formatBusinessDate(businessDay.data.lastClosedBusinessDate)}</>
                  ) : null}
                  . Anything keyed here is refused after the money has changed hands. Open the day
                  first.
                </>
              ) : (
                <>
                  {formatBusinessDate(businessDay.data.businessDate)} is {' '}
                  {businessDay.data.status === 'Closing' ? 'closing' : 'not open'}, so nothing new
                  posts into it.
                </>
              )}
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <Alert tone="info">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>
              Posting into {formatBusinessDate(businessDay.data.businessDate ?? '')}
            </AlertTitle>
            <AlertDescription>
              That is the branch&apos;s open day, which is the date the posting lands on. A value date
              may be backdated inside the institution&apos;s window; it is never the browser&apos;s
              date.
            </AlertDescription>
          </div>
        </Alert>
      )}

      {needsTill ? (
        <Alert tone={holdsTillPermission ? 'info' : 'warning'}>
          {holdsTillPermission ? <Wallet aria-hidden /> : <Banknote aria-hidden />}
          <div className="space-y-1">
            <AlertTitle>
              {holdsTillPermission ? 'This moves cash through your till' : 'Your role has no till permission for this'}
            </AlertTitle>
            <AlertDescription>
              {holdsTillPermission ? (
                <>
                  The posting resolves your own open counter session, or a field till assigned to you
                  for today, and is refused with{' '}
                  <span className="font-mono text-xs">teller.no_open_till</span> if you have neither.
                  A drawer over its holding limit refuses cash in with{' '}
                  <span className="font-mono text-xs">teller.till_cash_limit_exceeded</span>; one
                  without the notes refuses a payout with{' '}
                  <span className="font-mono text-xs">teller.till_insufficient_cash</span>. This screen
                  cannot see your drawer — open the till before the customer counts out the money.
                </>
              ) : (
                <>
                  A counter till also checks <span className="font-mono text-xs">{tillPermission}</span>,
                  which your role does not hold. Savings will accept the command and the cash drawer
                  will refuse it with{' '}
                  <span className="font-mono text-xs">auth.permission_denied</span> — after the customer
                  has handed the money over. Ask a teller to post this.
                </>
              )}
            </AlertDescription>
          </div>
        </Alert>
      ) : null}
    </div>
  )
}
